import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import AOS from "aos";
import "aos/dist/aos.css";
import "../CSS/animations.css";
import "../CSS/Home.css";
import FlipCard from "./FlipCard";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Carousel } from "primereact/carousel";
import { Skeleton } from "primereact/skeleton";

interface BlogPost {
  _id: string;
  title: string;
  text: string;
  imageUrl?: string;
  galleryImages: string[];
  createdAt: number;
  showInHome: boolean;
}

interface FlipCardItem {
  _id: string;
  title: string;
  text: string;
  imageUrl?: string;
  mainImage?: string;
  type: "car" | "ad" | "realestate";
  showInFlipcard: boolean;
}

const carouselItemTemplate = (item: { imageUrl: string }) => {
  return <img src={item.imageUrl} alt="carousel item" className="taswik-boy" />;
};

const Home = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [flipcardItems, setFlipcardItems] = useState<FlipCardItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const navigate = useNavigate();
  const toast = React.useRef<Toast>(null);

  const { user } = useAuth();

  const fetchPosts = async () => {
    try {
      axios
        .get(process.env.REACT_APP_API + "/posts")
        .then((response) => {
          setPosts(response.data);
        })
        .catch((error) => {
          console.error(error);
        });
    } catch (error) {
      console.error("Error fetching posts:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch blog posts",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchFlipcardItems = async () => {
    try {
      axios
        .get(process.env.REACT_APP_API + "/flipcard-items")
        .then((response) => {
          setFlipcardItems(response.data);
        })
        .catch((error) => {
          console.error(error);
        });
    } catch (error) {
      console.error("Error fetching flipcard items:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch flipcard items",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
    });
  }, []);

  useEffect(() => {
    fetchPosts();
    fetchFlipcardItems();
  }, []);

  const handleBlogClick = (postId: string) => {
    navigate(`/blog/${postId}`);
  };

  const handleEdit = (e: React.MouseEvent, postId: string) => {
    e.stopPropagation(); // Prevent triggering the card click
    navigate(`/edit-blog/${postId}`);
  };

  const confirmDelete = (e: React.MouseEvent, postId: string) => {
    e.stopPropagation(); // Prevent triggering the card click
    setSelectedPostId(postId);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!selectedPostId) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API}/posts/${selectedPostId}`
      );
      setShowDeleteDialog(false);

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Blog post deleted successfully",
        life: 3000,
      });
      await fetchPosts(); // Refresh the posts list
    } catch (error) {
      console.error("Error deleting post:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to delete blog post",
        life: 3000,
      });
    }
  };

  const isUserAuthorized = user !== null;

  const handleFlipCardClick = (id: string, type: string) => {
    const routes = {
      car: "/car",
      ad: "/ads",
      realestate: "/realestate",
    };
    navigate(`${routes[type as keyof typeof routes]}/${id}`);
  };

  const renderFlipCards = () => {
    if (flipcardItems.length === 0) {
      return (
        <div
          className="no-flipcards-message"
          style={{
            textAlign: "center",
            padding: "2rem",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            margin: "2rem 0",
          }}
        >
          <h3>No Flipcards Added Yet</h3>
          <p>
            Select items to display in the flipcard section by checking the
            "Show in Home Flipcard Section" option when creating or editing
            items.
          </p>
        </div>
      );
    }

    return (
      <div
        className="flipcards-container"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "1rem",
          justifyContent: "center",
          padding: "1rem",
        }}
      >   
        {flipcardItems.map((item) => (
          <FlipCard
            key={item._id}
            imageUrl={item.imageUrl || item.mainImage }
            title={item.title}
            description={item.text}
            onClick={() => handleFlipCardClick(item._id, item.type)}
          />
        ))}
      </div>
    );
  };

  const items = [
    { imageUrl: "./taswik-boy.png" },
    { imageUrl: "./taswik-girl.png" },
    { imageUrl: "./taswik-couple.png" },
  ];

  return (
    <div className="home-container">
      <Toast ref={toast} />
      <ConfirmDialog
        visible={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
        message="Are you sure you want to delete this blog post?"
        header="Confirm Deletion"
        icon="pi pi-exclamation-triangle"
        accept={handleDelete}
        reject={() => setShowDeleteDialog(false)}
      />

      <div className="hero-section">
        <div className="content-wrapper">
          <div className="title-container">
            <div className="left-content">
              {/* <div className="logoimage">
                <img src="./logohero.png" alt="Taswik Logo" />
              </div> */}
             
            </div>
            <div className="right-content">
              <div className="rotating-text">
                <p>Taswik is your gate way</p>
                <p>
                  <span className="word alizarin">
                    &nbsp;&nbsp;&nbsp;&nbsp;to the World.&nbsp;&nbsp;&nbsp;
                  </span>
                  <span className="word wisteria">
                    &nbsp;&nbsp;&nbsp;&nbsp;to
                    Success.&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  </span>
                  <span className="word peter-river">to the Evolution.</span>
                  <span className="word emerald">
                    &nbsp;&nbsp;&nbsp;&nbsp;to Creativity.&nbsp;&nbsp;
                  </span>
                </p>
              </div>
            </div>
            <div className="button-taswik">
                <Button
                  label="Get Started"
                  className="p-button-rounded p-button-success"
                  onClick={() => {
                    const phoneNumber = "+2250711711116";
                    const message = "book with the AI service now\n\nإحجز إعلانك بخدمة الذكاء الإصطناعي الآن";                  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
                    window.open(whatsappUrl, '_blank');
                  }}
                />
              </div>
          </div>
        </div>
      </div>

      {renderFlipCards()}

      <div className="blogs-section">
        <div className="blog-grid">
          {loading ? (
            <div className="no-posts">
              <i className="pi pi-spin pi-spinner text-4xl"></i>
            </div>
          ) : posts.length === 0 ? (
            <div className="no-posts">
              <h2>No blog posts available yet.</h2>
              <p>Check back later for new content!</p>
            </div>
          ) : (
            posts.map((post) => (
              <div
                key={post._id}
                className="card-hover-effect"
                data-aos="fade-up"
                onClick={() => handleBlogClick(post._id)}
              >
                <Card className="shadow-lg transition-transform duration-300 hover:shadow-xl cursor-pointer relative">
                  {isUserAuthorized && (
                    <div
                      className="absolute top-2 right-2 z-10 flex gap-2 bg-white/80 p-2 rounded-lg"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        icon="pi pi-pencil"
                        className="p-button-rounded p-button-text p-button-sm"
                        onClick={(e) => handleEdit(e, post._id)}
                        tooltip="Edit"
                      />
                      <Button
                        icon="pi pi-trash"
                        className="p-button-rounded p-button-text p-button-danger p-button-sm"
                        onClick={(e) => confirmDelete(e, post._id)}
                        tooltip="Delete"
                      />
                    </div>
                  )}
                  <div className="p-card-body" data-pc-section="body">
                    <div className="p-card-content" data-pc-section="content">
                      {post.imageUrl && (
                        <img
                          src={post.imageUrl}
                          alt={post.title}
                          className="w-full h-48 object-cover rounded-t-lg"
                        />
                      )}
                      <div className="p-4">
                        <h2 className="text-xl font-bold mb-2">{post.title}</h2>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;
